import { Avatar, Badge, Menu, MenuItem, MenuLabel } from "@payezy/ui";
import { Link, Outlet, useNavigate } from "@tanstack/react-router";
import { ArrowLeftRight, Building2, ClipboardCheck, Eye, FileCheck2, Gauge, KeyRound, LogOut, Radio, Scale, ScrollText, ShieldCheck, Users } from "lucide-react";
import { Suspense, type ReactNode } from "react";
import { kratos, reauthenticate } from "../lib/api";
import { useStaff } from "../lib/staff";

export function Brand() {
  return (
    <span className="inline-flex items-center gap-2">
      <span className="inline-flex size-7 items-center justify-center rounded-lg bg-amber-700 text-sm font-bold text-white">P</span>
      <span className="text-lg font-bold tracking-tight text-ink-950">Payezy</span>
      <Badge tone="warning">Staff</Badge>
    </span>
  );
}

type NavItem = { to: string; label: string; icon: ReactNode; perm?: string; exact?: boolean };

const nav: NavItem[] = [
  { to: "/", label: "Dashboard", icon: <Gauge className="size-4" />, exact: true },
  { to: "/customers", label: "Customers", icon: <Building2 className="size-4" />, perm: "customers:view" },
  { to: "/payments", label: "Payments", icon: <ArrowLeftRight className="size-4" />, perm: "payments:view" },
  { to: "/onboarding", label: "KYC / KYB queue", icon: <FileCheck2 className="size-4" />, perm: "onboarding:review" },
  { to: "/actions", label: "Actions & approvals", icon: <ClipboardCheck className="size-4" />, perm: "actions:request" },
  { to: "/view-as", label: "View-as sessions", icon: <Eye className="size-4" />, perm: "impersonate:read" },
  { to: "/recon", label: "Reconciliation", icon: <Scale className="size-4" />, perm: "recon:manage" },
  { to: "/partners", label: "Partners", icon: <Radio className="size-4" />, perm: "partners:view" },
  { to: "/audit", label: "Audit log", icon: <ScrollText className="size-4" />, perm: "audit:view" },
  { to: "/staff", label: "Staff", icon: <Users className="size-4" />, perm: "staff:manage" },
];

export function Shell() {
  const me = useStaff();
  const navigate = useNavigate();
  const visible = nav.filter((n) => !n.perm || me.can(n.perm));
  return (
    <div className="flex min-h-dvh">
      <aside className="sticky top-0 flex h-dvh w-60 shrink-0 flex-col border-r border-ink-200 bg-white px-3 py-4">
        <div className="px-2 pb-5"><Brand /></div>
        <nav className="flex flex-1 flex-col gap-0.5">
          {visible.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              activeOptions={{ exact: n.exact }}
              className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium text-ink-600 hover:bg-ink-100 hover:text-ink-900"
              activeProps={{ className: "bg-ink-100 text-ink-950" }}
            >
              {n.icon}
              {n.label}
            </Link>
          ))}
        </nav>
        <Menu
          align="start"
          trigger={
            <button className="flex w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-left hover:bg-ink-50">
              <Avatar name={me.name || me.email} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{me.name || me.email}</span>
                <span className="block truncate text-xs text-ink-500">{me.roles.join(", ")}</span>
              </span>
            </button>
          }
        >
          <MenuLabel>{me.email}</MenuLabel>
          <MenuItem onSelect={() => navigate({ to: "/security" })}>
            <KeyRound className="size-4" /> Passkeys & MFA
          </MenuItem>
          <MenuItem onSelect={reauthenticate}>
            <ShieldCheck className="size-4" /> {me.privileged ? "Re-authenticate" : "Re-authenticate for privileged actions"}
          </MenuItem>
          <MenuItem onSelect={() => void kratos.logout(window.location.origin + "/auth/login")}>
            <LogOut className="size-4" /> Sign out
          </MenuItem>
        </Menu>
      </aside>
      <main className="min-w-0 flex-1 bg-ink-50/40">
        <div className="mx-auto max-w-7xl px-8 py-8">
          <Suspense fallback={null}>
            <Outlet />
          </Suspense>
        </div>
      </main>
    </div>
  );
}
